import { canFinance, canManageUsers, canOpening, canSettlement } from './admin-access.ts'

// Pure config: the sidebar, the breadcrumb and the active-item match all derive from NAV, so they cannot drift.
// Relative imports only: node --test cannot resolve the @/ alias.

/** Open-item counts from GET /backoffice/{desk}/summary (same shape as DeskSummary in api-client). */
export type DeskCounts = {
  pending_agreements: number
  pending_deposits: number
  pending_withdrawals: number
  processing_funds: number
  ekyc_pending: number
}

export type Gate = (role: string[]) => boolean

export type NavIcon =
  | 'dashboard'
  | 'agreements'
  | 'funds'
  | 'bca'
  | 'accounts'
  | 'clientBanks'
  | 'companyBanks'
  | 'rates'
  | 'accsum'
  | 'mtUsers'
  | 'firstDeposits'
  | 'daily'
  | 'loginState'
  | 'activity'
  | 'ekyc'
  | 'users'

export interface NavChild {
  /** Path plus query, a filtered view of the parent list. */
  to: string
  label: string
  count?: (c: DeskCounts) => number
}

/** Filtered views the open-item counts point at, shared with the desk home pages so the list matches the number. */
export const PENDING_AGREEMENTS = '/opening/agreements?status=VERIFICATION_PENDING&status=FORM_SUBMITTED&status=DOCUMENTS_UPLOADED'
export const PENDING_FUNDS = '/settlement/funds?status=PENDING'
export const PENDING_EKYC = '/logs/ekyc?status=PENDING'

export interface NavItem {
  path: string
  /** Link target when it differs from `path` (a filtered view); `path` still decides the active match. */
  to?: string
  label: string
  icon: NavIcon
  /** Exact match only; otherwise deeper paths keep the item active. */
  end?: boolean
  /** Defaults to the group gate. */
  show?: Gate
  count?: (c: DeskCounts) => number
  children?: NavChild[]
  /** Breadcrumb label for one extra segment under `path`, e.g. /opening/agreements/:id. */
  detail?: string
}

export interface NavGroup {
  id: string
  /** Absent for top-level rows: no section header, no breadcrumb segment. */
  heading?: string
  show: Gate
  items: NavItem[]
}

// Every opening role is also a settlement role, so settlement is the home; Opening Home is a second row for them.
export function homePath(role: string[]): '/dash' | '/settlement' {
  return canManageUsers(role) ? '/dash' : '/settlement'
}

const everyone: Gate = () => true

export const NAV: NavGroup[] = [
  {
    id: 'home',
    show: everyone,
    items: [
      { path: '/dash', label: 'Dashboard', icon: 'dashboard', end: true, show: (r) => homePath(r) === '/dash' },
      { path: '/settlement', label: 'Settlement Home', icon: 'dashboard', end: true, show: (r) => homePath(r) === '/settlement' },
      { path: '/opening', label: 'Opening Home', icon: 'dashboard', end: true, show: (r) => canOpening(r) && !canManageUsers(r) },
    ],
  },
  {
    id: 'opening',
    heading: 'Opening',
    show: canOpening,
    items: [{ path: '/opening/agreements', to: PENDING_AGREEMENTS, label: 'Agreements', icon: 'agreements', detail: 'Review', count: (c) => c.pending_agreements }],
  },
  {
    id: 'settlement',
    heading: 'Settlement',
    show: canFinance,
    items: [
      {
        path: '/settlement/funds',
        to: PENDING_FUNDS,
        label: 'Funds',
        icon: 'funds',
        count: (c) => c.pending_deposits + c.pending_withdrawals,
        // Same links the desk home uses, so the list matches the number.
        children: [
          { to: '/settlement/funds?status=PENDING&kind=DEPOSIT', label: 'Deposits', count: (c) => c.pending_deposits },
          { to: '/settlement/funds?status=PENDING&kind=WITHDRAW', label: 'Withdrawals', count: (c) => c.pending_withdrawals },
          { to: '/settlement/funds?status=PROCESSING', label: 'Processing', count: (c) => c.processing_funds },
        ],
      },
      { path: '/settlement/bca', label: 'BCA Transactions', icon: 'bca' },
      { path: '/settlement/accounts', label: 'Live Accounts', icon: 'accounts', show: canSettlement },
      { path: '/settlement/client-banks', label: 'Client Banks', icon: 'clientBanks', show: canSettlement },
      { path: '/settlement/banks', label: 'Company Banks', icon: 'companyBanks' },
      { path: '/settlement/rates', label: 'Exchange Rates', icon: 'rates' },
    ],
  },
  {
    id: 'reports',
    heading: 'Reports',
    show: canSettlement,
    items: [
      { path: '/accsum', label: 'AccSum', icon: 'accsum' },
      { path: '/reports/mt-users', label: 'MT Users', icon: 'mtUsers' },
      { path: '/reports/first-deposits', label: 'First Deposits', icon: 'firstDeposits' },
      { path: '/reports/daily', label: 'Daily', icon: 'daily' },
      { path: '/reports/login-state', label: 'Login State', icon: 'loginState' },
    ],
  },
  {
    id: 'logs',
    heading: 'Logs',
    show: (r) => canOpening(r) || canFinance(r),
    items: [
      { path: '/logs/activity', label: 'Activity Logs', icon: 'activity' },
      { path: '/logs/ekyc', to: PENDING_EKYC, label: 'eKYC Logs', icon: 'ekyc', show: (r) => canOpening(r) || canSettlement(r), count: (c) => c.ekyc_pending },
    ],
  },
  { id: 'admin', heading: 'Admin', show: canManageUsers, items: [{ path: '/users', label: 'Users', icon: 'users' }] },
]

/** Groups and items this role may see; groups left empty are dropped. */
export function visibleNav(role: string[]): NavGroup[] {
  return NAV.filter((g) => g.show(role))
    .map((g) => ({ ...g, items: g.items.filter((i) => (i.show ?? g.show)(role)) }))
    .filter((g) => g.items.length > 0)
}

function matches(item: Pick<NavItem, 'path' | 'end'>, pathname: string): boolean {
  return pathname === item.path || (!item.end && pathname.startsWith(item.path + '/'))
}

function query(to: string): URLSearchParams {
  return new URLSearchParams(to.split('?')[1] ?? '')
}

/**
 * The `to` of the one entry that carries aria-current: a child when the URL's values for every query key
 * the siblings use equal that child's (empty where it sets none), otherwise the longest matching item path.
 */
export function activeNavTo(groups: NavGroup[], pathname: string, search: string): string | null {
  let best: NavItem | undefined
  for (const g of groups) for (const i of g.items) if (matches(i, pathname) && (!best || i.path.length > best.path.length)) best = i
  if (!best) return null
  const sp = new URLSearchParams(search)
  const children = best.children ?? []
  const keys = [...new Set(children.flatMap((c) => [...query(c.to).keys()]))]
  const child = children.find((c) => {
    const q = query(c.to)
    return keys.every((k) => sp.getAll(k).join(',') === q.getAll(k).join(','))
  })
  return child?.to ?? best.path
}

/** Breadcrumb for a pathname, role-agnostic: [heading?, label, detail?]; [] when nothing matches. */
export function trailFor(pathname: string): string[] {
  for (const g of NAV) {
    for (const i of g.items) {
      const head = g.heading ? [g.heading] : []
      if (pathname === i.path) return [...head, i.label]
      const rest = pathname.startsWith(i.path + '/') ? pathname.slice(i.path.length + 1) : ''
      if (i.detail && rest && !rest.includes('/')) return [...head, i.label, i.detail]
    }
  }
  return []
}
